import { FormEvent, useEffect, useState } from "react";
import { Edit, Eye, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminStaticPage,
  AdminUser,
  PaginationMeta,
  deleteAdminStaticPage,
  getCurrentAdmin,
  listAdminStaticPages,
  logout,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

/** 這兩個 Slug 目前有被 Footer 的隱私權政策／使用條款連結直接引用，刪除或改動前提醒管理者留意。 */
const linkedSlugs = new Set(["privacy-policy", "terms-of-service"]);

export default function AdminStaticPages() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminStaticPage[]>([]);
  const [counts, setCounts] = useState({ Published: 0, Hidden: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminStaticPages(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Published: 0, Hidden: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "自訂頁面資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function handleDelete(item: AdminStaticPage) {
    const isLinked = linkedSlugs.has(item.Slug);
    const confirmed = await confirm({
      title: "刪除自訂頁面？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Title}</strong> 嗎？
          <br />
          刪除後前台網址 /page/{item.Slug} 會立即失效，且無法復原。
          {isLinked && (
            <>
              <br />
              <span className="font-bold text-[#b3261e]">注意：網站頁尾目前有連結指向這個頁面，刪除後該連結會失效。</span>
            </>
          )}
        </>
      ),
      confirmText: "刪除頁面",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminStaticPage(item.StaticPageId);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "自訂頁面刪除失敗。");
    }
  }

  const allItemCount = counts.Published + counts.Hidden || pagination.Total;

  return (
    <AdminLayout sectionLabel="網站設定" title="自訂頁面管理" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        用來管理「隱私權政策」「使用條款」等純內容頁面，也可以新增其他頁面（例如退換貨政策、運送須知），前台網址為 /page/網址代稱。
      </p>

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">全部頁面</p>
          <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
        </button>
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Published", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">已發布</p>
          <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Published}</p>
        </button>
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Hidden", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">未發布</p>
          <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Hidden}</p>
        </button>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="static-page-status">狀態</label>
          <select id="static-page-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Published">已發布</option>
            <option value="Hidden">未發布</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="static-page-keyword">關鍵字</label>
          <input id="static-page-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="標題或網址代稱" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate("/admin/static-pages/new")} type="button">
          <Plus size={20} />
          新增頁面
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[820px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[280px] px-4 py-3">標題</th>
                <th className="w-[220px] px-4 py-3">網址代稱</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[150px] px-4 py-3">更新時間</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>沒有符合條件的頁面。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.StaticPageId}>
                  <td className="px-4 py-4">
                    <strong className="block truncate text-[#203d32]">{item.Title}</strong>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">
                    <code className="rounded bg-[#f0f3f1] px-1.5 py-0.5 text-xs">/page/{item.Slug}</code>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsPublished ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsPublished ? "已發布" : "未發布"}</span>
                  </td>
                  <td className="px-4 py-4 text-sm">{item.UpdatedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/static-pages/${item.StaticPageId}/edit`)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      {item.IsPublished && (
                        <a className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" href={`/page/${item.Slug}`} target="_blank" rel="noreferrer">
                          <Eye size={16} />
                          查看前台
                        </a>
                      )}
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        <Trash2 size={16} />
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      <ConfirmDialog />
    </AdminLayout>
  );
}
